import { FC, useEffect, useRef, useState } from 'react';
import { EntityData, InvestigationStatistics } from '../types';
import { EmptyState, EmptyStateIcons, Table, Tabs } from '@silverfort/sf-components';
import moment from 'moment';
import { ENTITY_STATISTIC_FILTER_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { INVESTIGATION_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { httpGet } from '../../utils';
import { AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { HTTP_OPTIONS } from '@silverfort-nodelibs/common/utils/consts';
import { HttpResponse } from '../../../api/types/Http';
import IdentityDataActivityLogs from './IdentityDataActivityLogs';
import IdentityDataActivityChart from '../Charts/IdentityDataActivityChart';
import IdentityDataActivityGraph from '../Charts/IdentityDataActivityGraph';

const SELECTED_TAB_CLASS =
    '[&>*]:bg-sf-gray-50 [&>*]:rounded-xl !border-hidden !border-b-[0px]  [&>*]:text-sf-gray-800';

const getEntityType = (adType) => {
    let entityType: number = INVESTIGATION_ENTITY_TYPES.USER.id;

    if (adType === AD_ENTITY_TYPES.HOST || adType === AD_ENTITY_TYPES.SERVER) {
        entityType = INVESTIGATION_ENTITY_TYPES.RESOURCE.id;
    }

    return entityType;
};

const IdentityDataActivityTab: FC<{
    entityData: EntityData;
    adType: number;
    onEntityClick: (guid?: string, identifier?: string, type?: number) => void;
}> = ({ entityData, adType, onEntityClick }) => {
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(false);
    const [activityByProtocol, setActivityByProtocol] = useState<
        { [key: string]: string | number; timestamp: number }[]
    >([]);
    const [protocolUsage, setProtocolUsage] = useState<{ count: number; protocol_type: string }[]>([]);
    const [totalAuths, setTotalAuths] = useState(0);
    const [daysFilter, setDaysFilter] = useState<string>('7');
    const tabRef = useRef(null);

    useEffect(() => {
        const getEntityStatistics = async () => {
            try {
                const filterStartDate = moment().subtract(Number(daysFilter), 'd');
                const filterEndDate = moment();
                const filters = {
                    [ENTITY_STATISTIC_FILTER_TYPES.TIME]: {
                        UTCStartDate: moment.utc(filterStartDate).unix(),
                        UTCEndDate: moment.utc(filterEndDate).unix()
                    }
                };

                const params = {
                    entityGuid: entityData.details.guid,
                    entityId: entityData.id,
                    entityType: getEntityType(adType),
                    filters: JSON.stringify(filters)
                };
                const response = (await httpGet('/investigate/statistic', {
                    [HTTP_OPTIONS.REQ_OPTIONS]: { params }
                })) as unknown as HttpResponse<InvestigationStatistics>;
                const statistics = response.data;

                if (!tabRef.current) {
                    return;
                }

                if (statistics?.activityByProtocol) {
                    setActivityByProtocol(statistics.activityByProtocol);
                }

                const tempUsageByProtocol = {};
                let tempTotalAuths = 0;
                if (statistics?.protocol_usages) {
                    setProtocolUsage(statistics.protocol_usages);
                    for (const usageData of statistics.protocol_usages) {
                        tempUsageByProtocol[usageData.protocol_type.toUpperCase()] = usageData.count;
                        tempTotalAuths += usageData.count;
                    }
                }
                setTotalAuths(tempTotalAuths);
                setIsLoading(false);
            } catch (e) {
                console.error('Failed to load entity statistics');
            }
        };

        if (entityData.id !== null) {
            setIsLoading(true);
            getEntityStatistics();
        }
    }, [adType, entityData, daysFilter]);

    const ACTIVITY_TABS = [
        {
            value: 0,
            name: 'General',
            tabClassName: selectedTab === 0 ? SELECTED_TAB_CLASS : undefined
        },
        { value: 1, name: 'Destinations', tabClassName: selectedTab === 1 ? SELECTED_TAB_CLASS : undefined },
        { value: 2, name: 'Sources', tabClassName: selectedTab === 2 ? SELECTED_TAB_CLASS : undefined },
        { value: 3, name: 'Logs', tabClassName: selectedTab === 3 ? SELECTED_TAB_CLASS : undefined }
    ];

    const renderTabData = () => {
        switch (selectedTab) {
            case 0: {
                return (
                    <div className="flex flex-col gap-4">
                        <IdentityDataActivityChart
                            protocolUsage={protocolUsage}
                            isLoading={isLoading}
                            totalAuths={totalAuths}
                            daysFilter={daysFilter}
                            setDaysFilter={setDaysFilter}
                        />
                        {!!totalAuths && (
                            <IdentityDataActivityGraph
                                daysFilter={daysFilter}
                                activityByProtocol={activityByProtocol}
                            />
                        )}
                    </div>
                );
            }
            case 1:
                return entityData.metrics?.topResources?.length ? (
                    <Table
                        rowClassName="!cursor-default"
                        disableHighlightedFirstColumn
                        columns={[
                            { columnKey: 'name', title: 'Name' },
                            { columnKey: 'count', title: 'Requests' }
                        ]}
                        rows={entityData.metrics?.topResources?.map((resource) => ({
                            name: resource.name || resource.identifier,
                            count: resource.count
                        }))}
                    />
                ) : (
                    <div className="w-full flex justify-center">
                        <EmptyState title="No Destinations" icon={EmptyStateIcons.NoItems} />
                    </div>
                );
            case 2:
                return entityData.metrics?.topDevices?.length ? (
                    <Table
                        disableHighlightedFirstColumn
                        rowClassName="!cursor-default"
                        columns={[
                            { columnKey: 'name', title: 'Name' },
                            { columnKey: 'count', title: 'Requests' }
                        ]}
                        rows={entityData.metrics?.topDevices?.map((device) => ({
                            name: device.name || device.identifier,
                            count: device.count
                        }))}
                    />
                ) : (
                    <div className="w-full flex justify-center">
                        <EmptyState title="No Sources" icon={EmptyStateIcons.NoItems} />
                    </div>
                );
            case 3:
                return (
                    <IdentityDataActivityLogs
                        onEntityClick={onEntityClick}
                        entityType={getEntityType(adType)}
                        entityData={entityData}
                    />
                );

            default:
                return <></>;
        }
    };

    return (
        <div ref={tabRef} className="border flex flex-col gap-4 h-full w-full border-sf-gray-100 py-2 px-6 rounded">
            <Tabs selectedTab={selectedTab} onClick={setSelectedTab} tabs={ACTIVITY_TABS} />
            {renderTabData()}
        </div>
    );
};

export default IdentityDataActivityTab;
